import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { OrganizationsAPI } from '@saraa/organizations/api/OrganizationsAPI.js';
import type { Organization } from '@saraa/organizations/entities/Organization.js';
import { organizationIdsOfUser, type SaraaUser } from '@saraa/permissions/db/SaraaUser.js';
import { TemplaterAPI } from '@saraa/templater/api/TemplaterAPI.js';
import { DOCUMENT_ACCEPT } from '@saraa/templater/documentFormats.js';
import { GenerateProcess } from '@saraa/templater/entities/GenerateProcess.js';
import type { Template } from '@saraa/templater/entities/Template.js';
import { TemporaryFile } from '@saraa/templater/entities/TemporaryFile.js';
import { html, nothing, type TemplateResult } from 'lit';
import { canReachAdminArea } from '../erm.js';
import { markGenerationStartedHere, openGenerateTasksDialog } from './generate-tasks-dialog.js';
import { SessionActions } from './session-actions.js';
import { emptyState, sectionLoader } from './ui.js';

const stepHeader = (step: number, title: string, hint?: string): TemplateResult => html`
  <div class="flex items-center gap-2.5">
    <span
      class="bg-primary/10 text-primary flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums"
    >
      ${step}
    </span>
    <h2 class="text-base-content font-semibold">
      ${title}${hint ? html`<span class="text-base-content/40 font-normal"> ${hint}</span>` : nothing}
    </h2>
  </div>
`;

/**
 * The tick is positioned rather than laid out, and only rendered when it applies.
 *
 * It used to sit in a row of its own above the name, kept in the flow at `opacity-0` while
 * unselected — so every card carried ~40px of blank space above its title whether or not
 * anything was ticked, which read as a rendering fault rather than a card. The right-hand
 * padding reserves the corner instead, so a long name never runs under the tick.
 *
 * Spans, not `div`/`p`: a button's content model is phrasing content, and the flow
 * elements this held before were not valid inside it.
 */
const templateCard = (template: Template, selected: boolean, onToggle: () => void): TemplateResult => html`
  <button
    type="button"
    aria-pressed=${selected ? 'true' : 'false'}
    class="rounded-box ${selected
      ? 'border-primary bg-primary/5'
      : 'border-base-300 bg-base-100 hover:border-primary/40 hover:bg-base-200/50'} relative flex flex-col gap-1 border p-4 pr-11 text-left"
    @click=${onToggle}
  >
    ${selected
      ? html`<span
          class="icon-[material-symbols--check-circle] text-primary absolute top-4 right-4 text-xl"
          aria-hidden="true"
        ></span>`
      : nothing}
    <span class="text-base-content font-medium">${template.name ?? 'Untitled template'}</span>
    ${template.description
      ? html`<span class="text-base-content/60 text-sm text-pretty">${template.description}</span>`
      : nothing}
  </button>
`;

/**
 * User-facing "generate" page — SARAA's AI document prefiller.
 *
 * Everything is scoped to one organization: it decides which templates are on
 * offer, which template-field catalog the extraction runs against, and which
 * extraction prompt and model are used. Users who belong to exactly one
 * organization never see that choice; anyone with more than one — and platform
 * admins, who typically belong to none — pick it first.
 *
 * Generating does not block the page: the request goes onto the task queue and the user follows it in the
 * task dialog, which they can close and reopen without affecting the run.
 */
export const GeneratePage = stateful(() => {
  const [process] = useState(new GenerateProcess({}));

  const { document, field, uploadAllPendingFiles } = useForm(process, { validator: null });

  const user = useObservable(() => currentUser<SaraaUser>(), []);
  const myOrganizationIds = organizationIdsOfUser(user);

  /**
   * The Admin button is offered on exactly the terms the admin routes are guarded
   * with — `canReachAdminArea` evaluates their requirements — so an ORG_USER, who
   * is refused by every one of them, is not sent to a shell that then turns them
   * away page by page.
   *
   * Keyed on the memberships and not just the id: the answer turns on the *role*
   * held in each one, so a user promoted to ORG_ADMIN has to re-evaluate, and
   * their id has not changed.
   */
  const membershipKey = (user?.memberships ?? [])
    .map(membership => `${membership.organizationId}:${membership.role}`)
    .join(',');
  const adminAreaQuery = useQuery(
    () => canReachAdminArea(),
    [user?._id, user?.isPlatformAdmin ?? false, membershipKey],
  );
  const canReachAdmin = adminAreaQuery.value === true;

  /**
   * `getMyOrganizations` already answers with the organizations the user may see
   * — everything for an admin, the ones they belong to otherwise — so the client
   * does not filter again.
   */
  const organizationsQuery = useQuery(
    () => OrganizationsAPI.getMine()({ _id: 1, name: 1 }),
    [user?._id, myOrganizationIds.join(',')],
  );
  const organizations = (organizationsQuery.value ?? []) as Organization[];

  // With exactly one organization there is nothing to choose, so pick it.
  if (!document.organizationId && organizations.length === 1) {
    document.organizationId = organizations[0]?._id;
  }
  const organizationId = document.organizationId;
  const needsOrganizationChoice = organizations.length > 1;

  // Templates are real entities now (managed in the ERM at /admin/templates).
  const templatesQuery = useQuery(
    () =>
      organizationId
        ? TemplaterAPI.subcribeTemplates(
            organizationId,
            // TODO: For Pilot, add searching and sorting
            0,
            20,
            new AdornisFilter({}),
          )({ _id: 1, name: 1, description: 1 })
        : Promise.resolve([]),
    [organizationId],
  );
  const templates = (templatesQuery.value ?? []) as Template[];

  /** The queue is the source of truth for what is running — this badge just counts what it reports. */
  const runningQuery = useQuery(() => TemplaterAPI.subscribeTasks()({ taskId: 1 }), []);
  const runningCount = (runningQuery.value ?? []).length;
  const [scheduling, setScheduling] = useState(false, []);

  const selectOrganization = (nextOrganizationId: string) => {
    if (scheduling || nextOrganizationId === document.organizationId) return;
    document.organizationId = nextOrganizationId;
    // Templates belong to one organization, so a switch invalidates the selection.
    document.templateIds = [];
  };

  const toggle = (id: string) => {
    if (scheduling) return;
    document.templateIds = document.templateIds?.includes(id)
      ? document.templateIds.filter(tid => tid !== id)
      : [...(document.templateIds || []), id];
  };

  const canGenerate = !!organizationId && !!document.templateIds?.length && !scheduling;

  const onGenerate = async () => {
    setScheduling(true);

    await guardDialog(
      async () => {
        await uploadAllPendingFiles();
        return TemplaterAPI.start(document)({ taskId: 1 });
      },
      {
        loadingText: 'Scheduling your generation…',
        errorMessagePrefix: 'Could not start the generation',
        success: async started => {
          if (started?.taskId) markGenerationStartedHere(started.taskId);
          await openGenerateTasksDialog();
        },
      },
    );

    setScheduling(false);
  };

  const organizationPicker = (): TemplateResult => html`
    <section class="flex flex-col gap-3">
      ${stepHeader(1, 'Choose organization')}
      <p class="text-base-content/60 text-sm">
        Templates, extraction fields and the extraction prompt all belong to one organization.
      </p>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        ${organizations.map(
          organization => html`
            <button
              type="button"
              aria-pressed=${organization._id === organizationId ? 'true' : 'false'}
              class="rounded-box ${organization._id === organizationId
                ? 'border-primary bg-primary/5'
                : 'border-base-300 bg-base-100 hover:border-primary/40 hover:bg-base-200/50'} flex items-center gap-2 border p-4 text-left"
              @click=${() => selectOrganization(organization._id)}
            >
              <span
                class="icon-[material-symbols--corporate-fare] ${organization._id === organizationId
                  ? 'text-primary'
                  : 'text-base-content/40'} text-lg"
              ></span>
              <span class="text-base-content font-medium">${organization.name ?? organization._id}</span>
            </button>
          `,
        )}
      </div>
    </section>
  `;

  const step = (n: number) => (needsOrganizationChoice ? n + 1 : n);

  return html`
    <div class="bg-base-200 h-screen w-full overflow-y-auto antialiased">
      <div class="mx-auto flex max-w-3xl flex-col gap-8 px-4 py-10 sm:py-14">
        <header class="flex items-start justify-between gap-4">
          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <span class="icon-[material-symbols--contract-edit-outline] text-primary text-3xl"></span>
              <h1 class="text-base-content text-3xl font-semibold tracking-tight">SARAA</h1>
            </div>
            <p class="text-base-content/60">AI-powered document prefiller for UK paperwork.</p>
          </div>
          <!-- Wraps rather than overflowing: "Stop impersonation" is a long label to fit beside three others on a phone. -->
          <div class="flex shrink-0 flex-wrap items-center justify-end gap-1">
            <button type="button" class="btn btn-ghost btn-sm gap-1.5" @click=${() => void openGenerateTasksDialog()}>
              <span class="icon-[material-symbols--list-alt-outline] text-base"></span> Tasks
              ${runningCount
                ? html`<span class="badge badge-primary badge-sm tabular-nums">${runningCount}</span>`
                : nothing}
            </button>
            ${canReachAdmin
              ? html`<a href="/admin" class="btn btn-ghost btn-sm gap-1.5">
                  <span class="icon-[material-symbols--settings-outline] text-base"></span> Admin
                </a>`
              : nothing}
            ${SessionActions({ layout: 'row' })}
          </div>
        </header>

        <div class="rounded-box border-base-300 bg-base-100 flex flex-col gap-8 border p-6 sm:p-8">
          ${organizationsQuery.loading
            ? sectionLoader()
            : organizations.length === 0
              ? emptyState(
                  'Your account does not belong to an organization yet, so there is nothing to generate. Ask an administrator to add you to one.',
                )
              : html`
                  ${needsOrganizationChoice ? organizationPicker() : nothing}

                  <!-- Templates -->
                  <section class="flex flex-col gap-4">
                    <div class="flex items-center justify-between gap-2">
                      ${stepHeader(step(1), 'Choose template(s)')}
                      ${document.templateIds?.length
                        ? html`<span class="text-base-content/60 text-sm tabular-nums"
                            >${document.templateIds.length} selected</span
                          >`
                        : nothing}
                    </div>
                    ${!organizationId
                      ? emptyState('Pick an organization first.')
                      : templatesQuery.loading
                        ? sectionLoader()
                        : templates.length === 0
                          ? emptyState(
                              html`No templates for this organization yet. Add one in
                                <a href="/admin/templates" class="link link-primary">Admin</a>.`,
                            )
                          : html`<div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                              ${templates.map(template =>
                                templateCard(template, document.templateIds?.includes(template._id) ?? false, () =>
                                  toggle(template._id),
                                ),
                              )}
                            </div>`}
                  </section>

                  <!-- Documents -->
                  <section class="flex flex-col gap-3">
                    ${stepHeader(step(2), 'Upload documents')}
                    <p class="text-base-content/60 text-sm">Add the source documents SARAA should read values from.</p>
                    ${FileInput({
                      ...field('documentIds'),
                      fileEntity: TemporaryFile,
                      accept: DOCUMENT_ACCEPT,
                      multi: true,
                      clearable: true,
                      placeholder: 'Drop files here or click to browse',
                    })}
                  </section>

                  <!-- Instructions -->
                  <section class="flex flex-col gap-3">
                    ${stepHeader(step(3), 'Custom instructions', '(optional)')}
                    ${TextareaInput({
                      placeholder: 'e.g. use UK date format (DD/MM/YYYY) and prefer the most recent document…',
                      placeholderMode: 'hide',
                      rows: 4,
                      ...field('additionalInstructions'),
                    })}
                  </section>

                  <!-- Generate -->
                  <div class="border-base-200 flex flex-col gap-3 border-t pt-6">
                    <button
                      type="button"
                      class="btn btn-primary btn-block gap-2"
                      ?disabled=${!canGenerate}
                      @click=${onGenerate}
                    >
                      ${scheduling
                        ? html`<span class="loading loading-spinner loading-sm"></span> Scheduling…`
                        : html`<span class="icon-[material-symbols--auto-awesome] text-lg"></span> Generate documents`}
                    </button>
                    <p class="text-base-content/50 text-center text-xs">
                      ${document.templateIds?.length
                        ? 'Runs in the background — you can keep working while it finishes.'
                        : 'Select at least one template to continue.'}
                    </p>
                  </div>
                `}
        </div>
      </div>
    </div>
  `;
});
